// @flow
import React, {Component} from 'react';
import {isEqual} from 'lodash';
import load from 'load-script';
import {connect} from 'react-redux';

import {
    configureHAdvertising,
    getAdSenseConfig,
    updateAdvertising,
    updateDisplayAds
} from '../HAdvertisingHelpers/HAdvertisingHelpers';
import {IFRAME_ADS, DEFAULT_SCREEN_SIZE} from '../../../constants/ads';

import type {ComponentType} from 'react';
import type {SearchQueryResponseData} from '../../../../../src/universal/queries/searchQuery';
import type {State as ReduxState} from '../../../reducers/index';
import cookieUtils from '../../../utils/cookieUtils';
import {HA_ADS_HOLDBACK_SERP_AND_PDP} from '../../../abacus/experiments';
import {evaluateExperimentAndLog} from '@homeaway/abacus-client-utils';

import './AdSense.less';

type StateProps = {
    isMobile: boolean,
    siteName: string,
    query: any,
    lastQuery: any,
    mapMoveState: boolean,
    searchTermUuid?: string
};

type ConnectorProps = StateProps & {
    adSense: {
        id: string,
        shouldShow: boolean,
        updateAdSense: () => void
    }
};

type State = {
    adsConfigured: boolean,
    adLibraryLoaded: boolean
};

const nonPageParams = ({page, ...rest}) => rest;

let personalizedAds;
const setPersonalizedAds = () => {
    personalizedAds = false;
    window.cookieconsent = window.cookieconsent || [];
    if (cookieUtils.readCookie('eu-site')) {
        // EU site and we need to check cookie consent
        window.cookieconsent.push((cookieAccess) => {
            if (cookieAccess.getConsents().functional) {
                // This is not instrumented properly
                // since setPersonalizedAds is always called when updatingAds and will be set to false
                // See https://jira.homeawaycorp.com/browse/SERP-1794
                personalizedAds = true;
            }
        });
    } else {
        // non-EU site, no need to check cookie consent
        personalizedAds = true;
    }
};

export default function withAdsense<
    OwnProps: {
        data: SearchQueryResponseData
    }
>(
    WrappedComponent: ComponentType<$Supertype<OwnProps>>
): ComponentType<OwnProps & ConnectorProps> {
    type Props = OwnProps & ConnectorProps;

    class WithAdSense extends Component<Props, State> {
        constructor(props) {
            super(props);

            this.state = {
                adLibraryLoaded:
                    typeof window !== 'undefined' ? !!window._googCsa : false,
                adsConfigured: false
            };
        }

        componentDidMount() {
            const {adLibraryLoaded} = this.state;
            const holdbackVal = this.getAdsHoldBackValue();
            const isAdsHoldback = holdbackVal === 1 || holdbackVal === 2;
            try {
                if (!adLibraryLoaded && !isAdsHoldback) {
                    this.loadAds();
                }
            } catch (e) {
                // ad blocker might be on, we don't need to do anything
            }
        }

        componentDidUpdate(prevProps: Props, prevState: State) {
            const {mapMoveState} = this.props;
            const {adLibraryLoaded, adsConfigured} = this.state;

            try {
                if (adLibraryLoaded && adsConfigured) {
                    this.updateHAdvertising(prevProps, prevState);
                }
            } catch (e) {
                // ad blocker might be on, we don't need to do anything
            }

            if (prevProps.mapMoveState !== mapMoveState) {
                this.changeAdsIframesPointer(mapMoveState ? 'none' : 'all');
            }
        }

        loadAds = () => {
            load('//www.google.com/adsense/search/async-ads.js', (err) => {
                // if there's an error loading ads, do we really care?
                if (!err) {
                    this.setState({adLibraryLoaded: true});

                    const adConfiguredCb = () => {
                        this.setState({adsConfigured: true});
                    };

                    configureHAdvertising(
                        {
                            isMobile: this.props.isMobile,
                            siteName: this.props.siteName,
                            screenWidth:
                                (window && window.innerWidth) ||
                                DEFAULT_SCREEN_SIZE
                        },
                        adConfiguredCb
                    );
                }
            });
        };

        /**
         * Updates the ads section along the bottom of page
         */
        updateAdSense = () => {
            const pageOptions = this.getPageOptions();
            if (pageOptions.searchTermUuid) {
                setPersonalizedAds();
                try {
                    window._googCsa(
                        'ads',
                        {...pageOptions, personalizedAds},
                        getAdSenseConfig()
                    );
                } catch (e) {
                    // ad blocker might be on, we don't need to do anything
                }
            }
        };

        getPageOptions = () => {
            const {
                data: {results = {}},
                searchTermUuid
            } = this.props;
            const {adSenseConfiguration = {}, hitCollection = {}} = results;

            return {
                ...adSenseConfiguration,
                searchTermUuid,
                adPage: hitCollection.page,
                linkTarget: '_blank',
                domainLinkAboveDescription: true,
                location: true,
                plusOnes: true
            };
        };

        updateHAdvertising = (prevProps: Props, prevState: State) => {
            const {
                query,
                lastQuery,
                data: {results, loading},
                query: {page}
            } = this.props;
            const {adLibraryLoaded, adsConfigured} = this.state;
            const prevData = prevProps.data;
            const prevResults = prevData.results;

            const previous = {
                page: lastQuery.page,
                results: prevResults,
                nonPageParams: nonPageParams(lastQuery),
                adsDisplayable:
                    prevState.adLibraryLoaded &&
                    prevState.adsConfigured &&
                    !prevData.loading
            };

            const current = {
                page,
                results,
                nonPageParams: nonPageParams(query),
                adsDisplayable: adLibraryLoaded && adsConfigured && !loading
            };

            // only display/refresh ads when we get new results
            const adsBecameDisplayable =
                current.adsDisplayable && !previous.adsDisplayable;
            const resultsChanged = !isEqual(previous.results, current.results);

            if (
                adsBecameDisplayable || // initial load of ads library after results have already come in
                (current.adsDisplayable && resultsChanged) // new results
            ) {
                const paged =
                    previous.page !== current.page &&
                    isEqual(previous.nonPageParams, current.nonPageParams);

                if (paged) {
                    // need to refresh ads for pagination
                    updateAdvertising();
                } else {
                    // need to display ads if the results changed or the ads library loaded
                    updateDisplayAds();
                }
            }
        };

        changeAdsIframesPointer(attribute) {
            IFRAME_ADS.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    element.style.pointerEvents = attribute;
                }
            });
        }

        getAdsHoldBackValue() {
            const evaluate = evaluateExperimentAndLog(
                HA_ADS_HOLDBACK_SERP_AND_PDP
            );
            return evaluate.value;
        }

        render() {
            const {
                searchTermUuid,
                data: {loading}
            } = this.props;
            return (
                <WrappedComponent
                    {...this.props}
                    adSense={{
                        updateAdSense: this.updateAdSense,
                        key: searchTermUuid,
                        shouldShow: !!(
                            !loading &&
                            searchTermUuid &&
                            this.state.adsConfigured &&
                            this.state.adLibraryLoaded
                        )
                    }}
                />
            );
        }
    }

    const mapStateToProps = ({
        map,
        searchParams: {params, prevParams},
        searchTerm: {id: searchTermUuid},
        system
    }: ReduxState) => ({
        isMobile: system.isMobile,
        siteName: system.siteName,
        query: params,
        lastQuery: prevParams,
        mapMoveState: map.mapMoveState,
        searchTermUuid
    });

    return connect(
        mapStateToProps,
        {}
    )(WithAdSense);
}
